import { createFileRoute } from "@tanstack/react-router";
import { OmnichannelPage } from "@/components/pages/OmnichannelPage";

const SITE = "https://kentro.com.br";

const META = {
  en: {
    title: "Omnichannel Strategy: The Guide to Transform Your Business",
    desc: "Understand what omnichannel is, why it matters and how to unify WhatsApp, Instagram and email in a single platform with AI and CRM. Kentro's full guide.",
    ogTitle: "Omnichannel Strategy: The Guide to Transform Your Business",
    ogDesc: "How to apply a real omnichannel strategy with WhatsApp, Instagram, email and voice unified.",
  },
  es: {
    title: "Estrategia Omnicanal: La Guía para Transformar tu Negocio",
    desc: "Entiende qué es omnicanal, por qué importa y cómo unificar WhatsApp, Instagram y e-mail en una sola plataforma con IA y CRM. Guía completa de Kentro.",
    ogTitle: "Estrategia Omnicanal: La Guía para Transformar tu Negocio",
    ogDesc: "Cómo aplicar una estrategia omnicanal real con WhatsApp, Instagram, e-mail y voz unificados.",
  },
} as const;

const FAQ = {
  en: [
    { q: "What's the difference between multichannel and omnichannel?", a: "In multichannel, channels work in isolation and the customer repeats their story. In omnichannel, channels talk to each other and the company already knows who the customer is and what they need." },
    { q: "Do I need multiple systems to be omnichannel?", a: "No. Kentro unifies WhatsApp, Instagram, Facebook, email, voice and webchat in a single platform, with integrated CRM and AI." },
    { q: "Is omnichannel for SMBs?", a: "Yes, especially for SMBs that serve a lot via WhatsApp — where channel fragmentation tends to be greatest." },
  ],
  es: [
    { q: "¿Cuál es la diferencia entre multicanal y omnicanal?", a: "En multicanal, los canales funcionan aislados y el cliente repite su historia. En omnicanal, los canales hablan entre sí y la empresa ya sabe quién es el cliente y qué necesita." },
    { q: "¿Necesito varios sistemas para ser omnicanal?", a: "No. Kentro unifica WhatsApp, Instagram, Facebook, e-mail, voz y webchat en una sola plataforma, con CRM e IA integrados." },
    { q: "¿El omnicanal sirve para PYMEs?", a: "Sí, especialmente para PYMEs que atienden mucho por WhatsApp — donde la fragmentación de canales suele ser mayor." },
  ],
} as const;

export const Route = createFileRoute("/$locale/omnichannel")({
  head: ({ params }) => {
    const loc: "en" | "es" = params.locale === "es" ? "es" : "en";
    const m = META[loc];
    return {
      meta: [
        { title: m.title },
        { name: "description", content: m.desc },
        { property: "og:title", content: m.ogTitle },
        { property: "og:description", content: m.ogDesc },
        { property: "og:url", content: `${SITE}/${loc}/omnichannel` },
        { property: "og:locale", content: loc === "es" ? "es_ES" : "en_US" },
      ],
      links: [
        { rel: "canonical", href: `${SITE}/${loc}/omnichannel` },
        { rel: "alternate", hrefLang: "pt-BR", href: `${SITE}/omnichannel` },
        { rel: "alternate", hrefLang: "en", href: `${SITE}/en/omnichannel` },
        { rel: "alternate", hrefLang: "es", href: `${SITE}/es/omnichannel` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}/omnichannel` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: loc === "es" ? "Inicio" : "Home", item: `${SITE}/${loc}/` },
              { "@type": "ListItem", position: 2, name: 'Omnichannel', item: `${SITE}/${loc}/omnichannel` },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": 'WebPage',
            "@id": `${SITE}/${loc}/omnichannel`,
            url: `${SITE}/${loc}/omnichannel`,
            name: m.title,
            description: m.desc,
            inLanguage: loc === "es" ? "es" : "en",
            isPartOf: { "@type": "WebSite", "@id": `${SITE}/#website` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ[loc].map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: OmnichannelPage,
});
